import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import CircularSpinner from 'shared/atoms/CircularSpinner';
import Icon from 'shared/atoms/Icon';

import theme from 'shared/themes/default';

import { useWatchData } from '../../../../Context';
import { listPlaylistUserWatchData } from '../../../../Context/graphql/queries';

const sharedStyles = {
  fontSize: '0.875rem',
  fontWeight: '600',
  lineHeight: '20px',
};

const sxListItemText = {
  '& .MuiListItemText-primary': {
    ...sharedStyles,
    color: theme.palette.disabledDark,
  },
  '& .MuiListItemText-secondary': {
    ...sharedStyles,
    color: theme.palette.pureDark,
  },
};

const getPalette = (completed, progress) => {
  if (completed) return theme.palette.successDark;
  if (progress === 0) return theme.palette.errorDark;
  return theme.palette.warning;
};

const PlaylistActivityList = ({ organizationId, playlistId, userId }) => {
  const { setWatchDataError, useLazyGraphQLQuery } = useWatchData();

  const [listPlaylistUserWatchDataQuery, listPlaylistUserWatchDataResponse] =
    useLazyGraphQLQuery(listPlaylistUserWatchData);

  const items =
    listPlaylistUserWatchDataResponse.data?.listPlaylistUserWatchData || [];

  useEffect(() => {
    listPlaylistUserWatchDataQuery({
      variables: {
        organizationId,
        playlistId,
        userId,
      },
    });
  }, []);

  useEffect(() => {
    const { error } = listPlaylistUserWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listPlaylistUserWatchDataResponse]);

  if (listPlaylistUserWatchDataResponse.loading) {
    return <CircularSpinner static thickness={2} size={25} />;
  }

  return (
    <List component="ul" aria-label="playlist activity list">
      {items.map((item, index) => {
        const isFirstElement = index === 0;
        const { id: courseId, name, progress, completedTime } = item;
        const secondary = `${progress}%`;
        return (
          <React.Fragment key={courseId}>
            {!isFirstElement && <Divider light />}
            <ListItem>
              <ListItemIcon>
                <Icon
                  icon={CheckCircleIcon}
                  color={getPalette(!!completedTime, progress)}
                />
              </ListItemIcon>
              <ListItemText
                primary={name}
                secondary={secondary}
                sx={sxListItemText}
              />
            </ListItem>
          </React.Fragment>
        );
      })}
    </List>
  );
};

PlaylistActivityList.propTypes = {
  organizationId: PropTypes.string.isRequired,
  playlistId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
};

export default PlaylistActivityList;
